// Debug which path is being taken
import { getAuthToken, auth } from './firebase';
import {
    buildListPrefetchQueue,
    maxCoveredIndex,
    selectLookaheadEntries,
} from './utils/patientPrefetchOrder';

// Calculate URLs based on current hostname
export const getApiUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('nginx-dev')) {
    return 'https://flask-dev-wsakhyk4pa-uc.a.run.app';
  } else if (hostname.includes('springhouseanalytics')) {
    console.log = function () {};
    console.debug = function () {};
    console.info = function () {};
    console.warn = function () {};
    console.error = function () {};
    return 'https://flask-prod-wsakhyk4pa-uc.a.run.app';
  } else {
    // Fallback for local development
    return 'http://localhost:5000';
  }
};

export const DATABASE_MODE_KEY = 'springhouseDatabaseMode';
export const DATABASE_MODES = {
  DEMO: 'demo',
  LIVE: 'live',
};

export function defaultDatabaseModeFromHost(hostname) {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (host.includes('nginx-dev') || host.startsWith('demo.')) {
    return DATABASE_MODES.DEMO;
  }
  return DATABASE_MODES.LIVE;
}

/** `/demo` (or `?db=demo`) forces demo; `/live` forces live. */
export function databaseModeFromLocation(pathname = '', search = '') {
  const path = String(pathname).replace(/\/+$/, '').toLowerCase() || '/';
  if (path === '/demo') return DATABASE_MODES.DEMO;
  if (path === '/live') return DATABASE_MODES.LIVE;
  const query = String(search).startsWith('?') ? String(search).slice(1) : String(search);
  const db = new URLSearchParams(query).get('db')
    || new URLSearchParams(query).get('database')
    || '';
  const mode = db.trim().toLowerCase();
  if (mode === DATABASE_MODES.DEMO || mode === DATABASE_MODES.LIVE) return mode;
  return null;
}

export function getDatabaseMode() {
  try {
    if (typeof window !== 'undefined') {
      const fromUrl = databaseModeFromLocation(window.location.pathname, window.location.search);
      if (fromUrl) return fromUrl;
    }
    const stored = localStorage.getItem(DATABASE_MODE_KEY);
    if (stored === DATABASE_MODES.DEMO || stored === DATABASE_MODES.LIVE) {
      return stored;
    }
  } catch (error) {
    // ignore storage errors
  }
  return defaultDatabaseModeFromHost();
}

export function setDatabaseMode(mode) {
  const next = mode === DATABASE_MODES.DEMO ? DATABASE_MODES.DEMO : DATABASE_MODES.LIVE;
  try {
    localStorage.setItem(DATABASE_MODE_KEY, next);
  } catch (error) {
    // ignore storage errors
  }
  return next;
}

/** Persist a `/demo` or `/live` launch link so later navigations keep that database. */
export function applyDatabaseLaunchPath() {
  const mode = getDatabaseMode();
  setDatabaseMode(mode);
  return mode;
}

export function syncDatabaseModePath(mode) {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const path = String(window.location.pathname || '/').replace(/\/+$/, '') || '/';
  const suffix = `${window.location.search || ''}${window.location.hash || ''}`;
  const onDemoPath = path === '/demo';
  const wantDemo = mode === DATABASE_MODES.DEMO;
  if (wantDemo && !onDemoPath) {
    window.history.replaceState(null, '', `/demo${suffix}`);
  } else if (!wantDemo && onDemoPath) {
    const params = new URLSearchParams(window.location.search || '');
    params.delete('db');
    params.delete('database');
    const query = params.toString();
    window.history.replaceState(null, '', `${query ? `/?${query}` : '/'}${window.location.hash || ''}`);
  }
}

export function getDatabaseRequestHeaders() {
  return { 'X-Database': getDatabaseMode() };
}

/** True when the active database is springhouse-demo. */
export const isDemoOrDevApp = () => getDatabaseMode() === DATABASE_MODES.DEMO;

export const DEMO_SESSION_RESET_KEY = 'springhouseDemoSessionReset';
let demoSessionResetInflight = null;

export function clearDemoSessionResetFlag() {
  demoSessionResetInflight = null;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(DEMO_SESSION_RESET_KEY);
    }
  } catch (error) {
    // ignore storage errors
  }
}

/** Once per demo browser session: drop synthetic MM recs and all patient notes. */
export async function prepareDemoSession() {
  if (getDatabaseMode() !== DATABASE_MODES.DEMO) {
    return { skipped: true, reason: 'not-demo' };
  }
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DEMO_SESSION_RESET_KEY) === '1') {
      return { skipped: true, reason: 'already-reset' };
    }
  } catch (error) {
    // ignore storage errors
  }
  if (!demoSessionResetInflight) {
    demoSessionResetInflight = (async () => {
      try {
        const result = await fetchWithAuth('/api/demo/reset-session', { method: 'POST' });
        if (result) {
          try {
            sessionStorage.setItem(DEMO_SESSION_RESET_KEY, '1');
          } catch (error) {
            // ignore storage errors
          }
        }
        return result || { skipped: true, reason: 'no-response' };
      } catch (error) {
        console.error('Demo session reset failed', error);
        return { skipped: true, reason: 'error' };
      } finally {
        demoSessionResetInflight = null;
      }
    })();
  }
  return demoSessionResetInflight;
}

const apiUrl = getApiUrl();
const config = { apiUrl};

// Auth state management
const authStateChangeCallbacks = [];
export const onAuthStateChange = (callback) => {
  authStateChangeCallbacks.push(callback);
};

export const notifyAuthStateChange = () => {
  authStateChangeCallbacks.forEach(callback => callback());
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('authToken');
};

// Patient data management
let patientData = null;
let patientDataCallbacks = [];

export const onPatientDataChange = (callback) => {
  patientDataCallbacks.push(callback);
  // Immediately call with current data if available
  if (patientData !== null) {
    callback(patientData);
  }
};

const notifyPatientDataChange = (data) => {
  patientData = data;
  patientDataCallbacks.forEach(callback => callback(data));
};

const jsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
};

// Cached Firebase JWT unless forceRefresh is true (e.g. after a 401).
const getAuthHeaders = async (forceRefresh = false) => {
  try {
    const token = await getAuthToken(forceRefresh);
    localStorage.setItem('authToken', token);
    return {
      ...jsonHeaders,
      ...getDatabaseRequestHeaders(),
      'Authorization': `Bearer ${token}`,
    };
  } catch (error) {
    return { ...jsonHeaders, ...getDatabaseRequestHeaders() };
  }
};

// Coalesce concurrent force-refreshes so parallel 401s share one token round trip.
let forceRefreshInflight = null;

const getAuthHeadersFresh = async () => {
  if (!forceRefreshInflight) {
    forceRefreshInflight = getAuthHeaders(true).finally(() => {
      forceRefreshInflight = null;
    });
  }
  return forceRefreshInflight;
};

// Generic fetch function with auth. Uses the cached ID token; force-refreshes once on 401.
export const fetchWithAuth = async (endpoint, options = {}) => {
  try {
    if (!auth.currentUser) {
      return null;
    }

    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const fullUrl = `${config.apiUrl}/${cleanEndpoint}`;

    const doFetch = async (headers) => fetch(fullUrl, {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    });

    let response = await doFetch(await getAuthHeaders());

    if (response.status === 401) {
      try {
        const refreshedHeaders = await getAuthHeadersFresh();
        if (!refreshedHeaders.Authorization) {
          throw new Error('Token refresh failed');
        }
        response = await doFetch(refreshedHeaders);
      } catch (error) {
        throw new Error('Failed to fetch data after token refresh');
      }
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch data');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export async function fetchAllPatients() {
    try {
        const data = await fetchWithAuth('/patients/all');
        if (data) {
            notifyPatientDataChange(data);
        }
        return data;
    } catch (error) {
        return [];
    }
}

const patientByIdCache = new Map();
const jdCharacteristicsCache = new Map();
const recommendationsNavInflight = new Map();
const recommendationByIdCache = new Map();
const patientPrefetchEpoch = new Map();
const prefetchStartedIds = new Set();
let prefetchPendingEntries = [];
let prefetchPumpRunning = false;
let prefetchPumpTail = Promise.resolve();
let phiCacheGeneration = 0;

function getPatientPrefetchWindowSize() {
    return isDemoOrDevApp() ? 10 : 5;
}

function getPatientPrefetchEpoch(patientId) {
    return patientPrefetchEpoch.get(patientId) || 0;
}

function dropCacheIfCurrent(cache, key, pending) {
    if (cache.get(key) === pending) {
        cache.delete(key);
    }
}

function invalidatePatientPrefetch(patientId) {
    if (!patientId) return;
    patientPrefetchEpoch.set(patientId, getPatientPrefetchEpoch(patientId) + 1);
    prefetchStartedIds.delete(patientId);
    prefetchPendingEntries = prefetchPendingEntries.filter((entry) => entry.id !== patientId);
    patientByIdCache.delete(patientId);
    jdCharacteristicsCache.delete(patientId);
    recommendationsNavInflight.delete(patientId);
    for (const key of [...recommendationByIdCache.keys()]) {
        if (key.startsWith(`${patientId}:`)) {
            recommendationByIdCache.delete(key);
        }
    }
}

export async function fetchPatientById(patientId) {
    if (!patientId) return null;
    if (patientByIdCache.has(patientId)) {
        return patientByIdCache.get(patientId);
    }
    const epoch = getPatientPrefetchEpoch(patientId);
    const gen = phiCacheGeneration;
    let pending;
    pending = (async () => {
        try {
            return await fetchWithAuth(`/patients/${patientId}`);
        } catch (error) {
            return null;
        }
    })().then((data) => {
        if (gen !== phiCacheGeneration || !data || getPatientPrefetchEpoch(patientId) !== epoch) {
            dropCacheIfCurrent(patientByIdCache, patientId, pending);
        }
        return data;
    });
    patientByIdCache.set(patientId, pending);
    return pending;
}

/**
 * Active + inactive appointment nav in one HTTP round trip.
 * Concurrent callers for the same patientId share one in-flight / cached request.
 */
export async function fetchRecommendationsNav(patientId) {
    if (!patientId) {
        return { active: [], inactive: [] };
    }
    if (recommendationsNavInflight.has(patientId)) {
        return recommendationsNavInflight.get(patientId);
    }
    const epoch = getPatientPrefetchEpoch(patientId);
    const gen = phiCacheGeneration;
    let pending;
    pending = (async () => {
        try {
            const data = await fetchWithAuth(`/patients/${patientId}/recommendations/nav`);
            return {
                active: Array.isArray(data?.active) ? data.active : [],
                inactive: Array.isArray(data?.inactive) ? data.inactive : [],
            };
        } catch (error) {
            console.error('fetchRecommendationsNav:', error);
            dropCacheIfCurrent(recommendationsNavInflight, patientId, pending);
            return { active: [], inactive: [] };
        }
    })().then((data) => {
        if (gen !== phiCacheGeneration || getPatientPrefetchEpoch(patientId) !== epoch) {
            dropCacheIfCurrent(recommendationsNavInflight, patientId, pending);
        }
        return data;
    });
    recommendationsNavInflight.set(patientId, pending);
    return pending;
}

/** Call when the user selects a patient row so nav loads in parallel with patient detail. */
export function prefetchRecommendationsNav(patientId) {
    if (!patientId) return;
    void fetchRecommendationsNav(patientId);
}

/** One row per distinct active appointment_id — uses combined /recommendations/nav. */
export async function fetchActiveRecommendationsNav(patientId) {
    const { active } = await fetchRecommendationsNav(patientId);
    return active;
}

/** Archived (inactive) recommendations — uses combined /recommendations/nav. */
export async function fetchInactiveRecommendationsNav(patientId) {
    const { inactive } = await fetchRecommendationsNav(patientId);
    return inactive;
}

/** Deactivate all active recommendation rows for this appointment_id (patient-scoped). */
export async function deactivateRecommendationsForAppointment(patientId, appointmentId) {
    const result = await fetchWithAuth(`/patients/${patientId}/recommendations/deactivate`, {
        method: 'POST',
        body: JSON.stringify({ appointment_id: appointmentId }),
    });
    invalidatePatientPrefetch(patientId);
    return result;
}

/**
 * Load full recommendation JSON by id (scoped to patient).
 * @param {{ inactive?: boolean }} options - set inactive:true for archived rows (active = false).
 */
export async function fetchRecommendationById(patientId, recommendationId, options = {}) {
    const q = options.inactive ? '?inactive=true' : '';
    if (!patientId || recommendationId == null || recommendationId === '') {
        return await fetchWithAuth(`/patients/${patientId}/recommendations/${recommendationId}${q}`);
    }
    const cacheKey = `${patientId}:${recommendationId}:${options.inactive ? 'i' : 'a'}`;
    if (recommendationByIdCache.has(cacheKey)) {
        return recommendationByIdCache.get(cacheKey);
    }
    const epoch = getPatientPrefetchEpoch(patientId);
    const gen = phiCacheGeneration;
    let pending;
    pending = (async () => {
        return await fetchWithAuth(`/patients/${patientId}/recommendations/${recommendationId}${q}`);
    })().then((data) => {
        if (gen !== phiCacheGeneration || getPatientPrefetchEpoch(patientId) !== epoch) {
            dropCacheIfCurrent(recommendationByIdCache, cacheKey, pending);
        }
        return data;
    }).catch((error) => {
        dropCacheIfCurrent(recommendationByIdCache, cacheKey, pending);
        throw error;
    });
    recommendationByIdCache.set(cacheKey, pending);
    return pending;
}

export async function fetchJointDecisionsData(patientId) {
    try {
        return await fetchWithAuth(`/patients/${patientId}/jointdecisions`);
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching joint decisions data:', error);
        return null;
    }
}

export async function fetchRegressionCoefficients() {
    try {
        return await fetchWithAuth(`/patients/jointdecisions/coefficients`);
    } catch (error) {
        console.error('Error fetching regression coefficients:', error);
        return null;
    }
}

export async function fetchComplicationRates(patientId) {
    try {
        const res = await fetchWithAuth(`/patients/${patientId}/complication-rates`);
        return res?.complication_rates ?? {};
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching complication rates:', error);
        return {};
    }
}

/** Eligible patients and phase flags for a study pipeline. */
export async function fetchStudyPipeline(study) {
  if (!study) return { study, patients: [] };
  return fetchWithAuth(`/patients/study-pipeline?study=${encodeURIComponent(study)}`);
}

export async function enrollPatientInStudies(patientId, studies) {
  if (!patientId || !studies?.length) return null;
  return fetchWithAuth(`/patients/${encodeURIComponent(patientId)}/study-pipeline/enroll`, {
    method: 'POST',
    body: JSON.stringify({ studies }),
  });
}

export async function setStudyEnrollmentDeclined(patientId, study, declined) {
  if (!patientId) return null;
  return fetchWithAuth(`/patients/${encodeURIComponent(patientId)}/study-pipeline/decline`, {
    method: 'POST',
    body: JSON.stringify({ study, declined: Boolean(declined) }),
  });
}

export async function fetchStudyChart(patientId) {
  if (!patientId) return null;
  return fetchWithAuth(`/patients/${encodeURIComponent(patientId)}/study-chart`);
}

export async function saveStudyChart(patientId, chart, { signal } = {}) {
  if (!patientId) return null;
  return fetchWithAuth(`/patients/${encodeURIComponent(patientId)}/study-chart`, {
    method: 'PUT',
    body: JSON.stringify(chart || {}),
    signal,
  });
}

export async function fetchStudyChartFacts(patientId, indexDate) {
    if (!patientId) return null;
    try {
        const body = {};
        if (indexDate) body.index_date = indexDate;
        const data = await fetchWithAuth(`/patients/${patientId}/studies/chart-facts`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
        if (!data || data.error) return null;
        return data;
    } catch (error) {
        console.error('[Studies][API] Error fetching chart facts:', error);
        return null;
    }
}

export async function fetchJointDecisionsSpreadsheetCharacteristics(patientId) {
    if (!patientId) return null;
    if (jdCharacteristicsCache.has(patientId)) {
        return jdCharacteristicsCache.get(patientId);
    }
    const epoch = getPatientPrefetchEpoch(patientId);
    const gen = phiCacheGeneration;
    let pending;
    pending = (async () => {
        try {
            return await fetchWithAuth(`/patients/${patientId}/jointdecisions/spreadsheet/characteristics`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
        } catch (error) {
            console.error('[JointDecisions][API] Error fetching spreadsheet characteristics:', error);
            return null;
        }
    })().then((data) => {
        if (gen !== phiCacheGeneration || !data || getPatientPrefetchEpoch(patientId) !== epoch) {
            dropCacheIfCurrent(jdCharacteristicsCache, patientId, pending);
        }
        return data;
    });
    jdCharacteristicsCache.set(patientId, pending);
    return pending;
}

/**
 * Load strata OLS regression_models.json.
 * Prefer authenticated Flask (GCS/local); fall back to same-origin
 * public/jointdec-strata/regression_models.json for local CRA / nginx static.
 * Payload is global — cached after the first successful load.
 */
let jointDecisionsRegressionModelsPromise = null;

async function loadJointDecisionsRegressionModels(patientId) {
    if (patientId) {
        try {
            const payload = await fetchWithAuth(
                `/patients/${patientId}/jointdecisions/spreadsheet/regression-models`,
                {
                    method: 'POST',
                    body: JSON.stringify({}),
                },
            );
            if (payload && !payload.error && payload.outcomes) {
                return payload;
            }
            console.warn(
                '[JointDecisions][API] Regression models API unavailable:',
                payload?.error || 'empty payload',
            );
        } catch (error) {
            console.warn('[JointDecisions][API] Regression models API failed, trying static fallback:', error);
        }
    }
    try {
        const res = await fetch('/jointdec-strata/regression_models.json', {
            method: 'GET',
            cache: 'no-cache',
        });
        if (!res.ok) {
            console.error('[JointDecisions][API] Static regression_models.json HTTP', res.status);
            return null;
        }
        const payload = await res.json();
        if (!payload?.outcomes) {
            console.error('[JointDecisions][API] Static regression_models.json missing outcomes');
            return null;
        }
        return payload;
    } catch (error) {
        console.error('[JointDecisions][API] Error loading static regression models:', error);
        return null;
    }
}

export async function fetchJointDecisionsPracticeStats(patientId, options = {}) {
    if (!patientId) return null;
    try {
        const payload = await fetchWithAuth(
            `/patients/${patientId}/jointdecisions/spreadsheet/practice-stats`,
            {
                method: 'POST',
                body: JSON.stringify({
                    selectors: options.selectors || {},
                    procedure_keys: options.procedureKeys || [],
                }),
            },
        );
        if (!payload || payload.error) {
            console.error('[JointDecisions][API] Practice stats error:', payload?.error);
            return null;
        }
        return payload;
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching practice stats:', error);
        return null;
    }
}

export async function fetchJointDecisionsRegressionModels(patientId) {
    if (jointDecisionsRegressionModelsPromise) {
        return jointDecisionsRegressionModelsPromise;
    }
    const pending = loadJointDecisionsRegressionModels(patientId).then((payload) => {
        if (!payload?.outcomes) {
            if (jointDecisionsRegressionModelsPromise === pending) {
                jointDecisionsRegressionModelsPromise = null;
            }
        }
        return payload;
    });
    jointDecisionsRegressionModelsPromise = pending;
    return pending;
}

/**
 * Load a slim sex×age strata JSON slice via authenticated Flask API
 * (private GCS on the server — not a public URL).
 * @param {string} patientId
 * @param {{ sexCode: number, ageBucket: number }} options
 * @returns {Promise<{ rows: object[], sex?: number, age_bucket?: number }|null>}
 */
export async function fetchJointDecisionsStrataSlice(patientId, options = {}) {
    const sexCode = Number(options.sexCode);
    const ageBucket = Number(options.ageBucket);
    if (!patientId || !Number.isFinite(sexCode) || !Number.isFinite(ageBucket)) {
        return null;
    }
    try {
        const payload = await fetchWithAuth(
            `/patients/${patientId}/jointdecisions/spreadsheet/strata-slice`,
            {
                method: 'POST',
                body: JSON.stringify({
                    sex: sexCode,
                    age_bucket: ageBucket,
                }),
            },
        );
        if (!payload || payload.error) {
            console.error('[JointDecisions][API] Strata slice error:', payload?.error);
            return null;
        }
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        return {
            rows,
            sex: payload.sex ?? sexCode,
            age_bucket: payload.age_bucket ?? ageBucket,
        };
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching strata slice:', error);
        return null;
    }
}

/** Organization-level Joint Decisions procedure visibility config. Cached after first success. */
let organizationJointDecisionsProcedureConfigPromise = null;

export async function fetchOrganizationJointDecisionsProcedureConfig() {
    if (organizationJointDecisionsProcedureConfigPromise) {
        return organizationJointDecisionsProcedureConfigPromise;
    }
    const pending = (async () => {
        try {
            return await fetchWithAuth('/api/organization/joint-decisions/procedures');
        } catch (error) {
            console.error('[JointDecisions][API] Error fetching organization procedure config:', error);
            return null;
        }
    })().then((data) => {
        if (!data) {
            if (organizationJointDecisionsProcedureConfigPromise === pending) {
                organizationJointDecisionsProcedureConfigPromise = null;
            }
        }
        return data;
    });
    organizationJointDecisionsProcedureConfigPromise = pending;
    return pending;
}

/**
 * Warm global Joint Decisions payloads while the patient list is showing.
 * Models use an optional patientId so production can hit the authenticated Flask route.
 */
export function prefetchJointDecisionsGlobals(patientId) {
    void fetchOrganizationJointDecisionsProcedureConfig();
    void fetchJointDecisionsRegressionModels(patientId);
}

function pickPrefetchRecommendationId(nav, appointmentId) {
    const active = Array.isArray(nav?.active) ? nav.active : [];
    if (appointmentId != null && appointmentId !== '') {
        const wanted = String(appointmentId);
        const match = active.find(
            (item) => item?.appointment_id != null && String(item.appointment_id) === wanted,
        );
        if (match?.recommendation_id != null) return match.recommendation_id;
    }
    return active[0]?.recommendation_id ?? null;
}

async function prefetchPatientWorkspace(patientId, options = {}) {
    if (!patientId) return;
    const gen = phiCacheGeneration;
    const includeCharacteristics = !!options.includeCharacteristics;
    try {
        const [nav] = await Promise.all([
            fetchRecommendationsNav(patientId),
            fetchPatientById(patientId),
            includeCharacteristics
                ? fetchJointDecisionsSpreadsheetCharacteristics(patientId)
                : Promise.resolve(null),
        ]);
        if (gen !== phiCacheGeneration) return;
        const recommendationId = pickPrefetchRecommendationId(nav, options.appointmentId);
        if (recommendationId != null) {
            try {
                await fetchRecommendationById(patientId, recommendationId);
            } catch (error) {
                console.warn('[prefetch] recommendation failed:', patientId, recommendationId, error);
            }
        }
    } catch (error) {
        console.warn('[prefetch] workspace failed:', patientId, error);
    }
}

/**
 * Serial background warmup. Login starts the first window of Joint Decisions
 * upcoming patients; opening a row keeps the next windowSize names warm.
 * Demo/dev window is 10; production is 5.
 */
function pumpPrefetchQueue() {
    if (prefetchPumpRunning) return prefetchPumpTail;
    prefetchPumpRunning = true;
    prefetchPumpTail = (async () => {
        const gen = phiCacheGeneration;
        try {
            while (prefetchPendingEntries.length) {
                if (gen !== phiCacheGeneration) break;
                const entry = prefetchPendingEntries.shift();
                if (!entry?.id || prefetchStartedIds.has(entry.id)) continue;
                prefetchStartedIds.add(entry.id);
                await prefetchPatientWorkspace(entry.id, {
                    includeCharacteristics: entry.includeCharacteristics,
                    appointmentId: entry.appointmentId,
                });
            }
        } finally {
            prefetchPumpRunning = false;
        }
        if (prefetchPendingEntries.length) {
            return pumpPrefetchQueue();
        }
        return undefined;
    })();
    return prefetchPumpTail;
}

/** Drop in-memory patient PHI so logout cannot leave charts in the tab. */
export function resetPatientWorkspacePrefetch() {
    phiCacheGeneration += 1;
    prefetchPendingEntries = [];
    prefetchStartedIds.clear();
    patientByIdCache.clear();
    jdCharacteristicsCache.clear();
    recommendationsNavInflight.clear();
    recommendationByIdCache.clear();
    patientPrefetchEpoch.clear();
    patientData = null;
}

export function startPatientListPrefetch(patients, listKind) {
    const queue = buildListPrefetchQueue(patients, listKind);
    prefetchPendingEntries = selectLookaheadEntries(
        queue,
        0,
        getPatientPrefetchWindowSize(),
        prefetchStartedIds,
    );
    return pumpPrefetchQueue();
}

export function extendPatientListPrefetch(patients, listKind, patientId) {
    const queue = buildListPrefetchQueue(patients, listKind);
    const clickedIndex = queue.findIndex((entry) => entry.id === patientId);
    const covered = maxCoveredIndex(queue, prefetchStartedIds, prefetchPendingEntries);
    if (patientId) prefetchStartedIds.add(patientId);
    if (clickedIndex < 0) {
        prefetchPendingEntries = selectLookaheadEntries(
            queue,
            0,
            getPatientPrefetchWindowSize(),
            prefetchStartedIds,
        );
        return pumpPrefetchQueue();
    }
    const isJump = covered < 0 ? clickedIndex > 0 : clickedIndex > covered;
    const fromIndex = isJump ? clickedIndex + 1 : 0;
    prefetchPendingEntries = selectLookaheadEntries(
        queue,
        fromIndex,
        getPatientPrefetchWindowSize(),
        prefetchStartedIds,
    );
    return pumpPrefetchQueue();
}

export async function fetchJointDecisionsValue() {
    try {
        return await fetchWithAuth('/api/organization/joint-decisions/value');
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching value lookup:', error);
        return null;
    }
}

export async function saveOrganizationJointDecisionsProcedureConfig(hiddenProcedures) {
    try {
        const result = await fetchWithAuth('/api/organization/joint-decisions/procedures', {
            method: 'PUT',
            body: JSON.stringify({ hidden_procedures: hiddenProcedures }),
        });
        if (result && !result.error) {
            organizationJointDecisionsProcedureConfigPromise = Promise.resolve(result);
        } else {
            organizationJointDecisionsProcedureConfigPromise = null;
        }
        return result;
    } catch (error) {
        organizationJointDecisionsProcedureConfigPromise = null;
        console.error('[JointDecisions][API] Error saving organization procedure config:', error);
        throw error;
    }
}

/** Latest Joint Decisions encounter decision for this patient (null if none). */
export async function fetchJointDecisionsDecision(patientId, appointmentId) {
    if (!patientId) return null;
    try {
        const params = new URLSearchParams({ patient_id: String(patientId) });
        if (appointmentId) params.set('appointment_id', String(appointmentId));
        const result = await fetchWithAuth(`/patients/jointdecisions/decisions?${params.toString()}`);
        return result?.decision || null;
    } catch (error) {
        console.error('[JointDecisions][API] Error fetching encounter decision:', error);
        return null;
    }
}

/** Append-only save of a Joint Decisions encounter decision. */
export async function saveJointDecisionsDecision(payload) {
    return fetchWithAuth('/patients/jointdecisions/decisions', {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function calculatePeriopSchedule(text, patientId = null, appointmentId = null) {
    try {
        const body = { text, patient_id: patientId };
        if (patientId && appointmentId) {
            body.appointment_id = appointmentId;
        }
        return await fetchWithAuth('/periop/schedule/recommend', {
            method: 'POST',
            body: JSON.stringify(body)
        });
    } catch (error) {
        throw error;
    }
}

export async function generateRandomPatient(hints) {
    try {
        return await fetchWithAuth('/periop/patient/notes/random', {
            method: 'POST',
            body: JSON.stringify({ hints })
        });
    } catch (error) {
        return { error: 'An error occurred while generating a random patient.' };
    }
}

export const savePatientNote = async (patientId, noteText, added, removed, appointmentId, options = {}) => {
  console.log("API.JS SAVE PATIENT NOTE")
  console.log("Added:", added)
  console.log("Removed:", removed)
  try {
    if (!appointmentId) {
      throw new Error('appointment_id is required to save a patient note');
    }
    // Ensure added and removed are arrays
    const formattedAdded = Array.isArray(added) ? added : [];
    const formattedRemoved = Array.isArray(removed) ? removed : [];

    // Convert arrays to JSON strings to match Python's json.dumps()
    const addedJson = JSON.stringify(formattedAdded);
    const removedJson = JSON.stringify(formattedRemoved);

    const { additionalSurgery, clearAdditionalSurgery } = options;

    const result = await fetchWithAuth(`/periop/api/patients/${patientId}/notes`, {
      method: 'POST',
      body: JSON.stringify({
        note_text: noteText,
        action_type: 'NOTE',
        added: addedJson,
        removed: removedJson,
        appointment_id: appointmentId,
        ...(additionalSurgery != null && { additional_surgery: additionalSurgery }),
        ...(clearAdditionalSurgery && { clear_additional_surgery: true })
      })
    });
    invalidatePatientPrefetch(patientId);
    return result;
  } catch (error) {
    console.error("Error in savePatientNote:", error);
    throw error;
  }
};

export const getPatientNotes = async (patientId) => {
  try {
    return await fetchWithAuth(`/periop/api/patients/${patientId}/notes`);
  } catch (error) {
    return [];
  }
};

export const deletePatientNote = async (patientId, noteId, patientText, isLastNote, appointmentId) => {
  try {
    const body = {
      patient_text: patientText,
      is_last_note: isLastNote
    };
    if (appointmentId) {
      body.appointment_id = appointmentId;
    }
    const result = await fetchWithAuth(`/periop/api/patients/${patientId}/notes/${noteId}`, {
      method: 'DELETE',
      body: JSON.stringify(body)
    });
    invalidatePatientPrefetch(patientId);
    return result;
  } catch (error) {
    return { success: false, error: 'Network error while deleting note' };
  }
};

export const updateMessageStatus = async (patientId, status, note, appointmentId) => {
  console.log('=== updateMessageStatus API CALL ===');
  console.log('Parameters:', { patientId, status, note, appointmentId });
  
  try {
    if (appointmentId == null || appointmentId === '') {
      throw new Error('appointment_id is required to approve or deny');
    }
    const endpoint = `/periop/api/messages/patient/${patientId}/status`;
    console.log('API endpoint:', endpoint);
    
    const requestBody = {
      status,
      note,
      appointment_id: typeof appointmentId === 'string' ? appointmentId : String(appointmentId)
    };
    console.log('Request body:', requestBody);
    
    const result = await fetchWithAuth(endpoint, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    console.log('API response:', result);
    invalidatePatientPrefetch(patientId);
    return result;
  } catch (error) {
    console.error('API call failed:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw error;
  }
};

export const getPatientMessages = async (patientId) => {
  try {
    const response = await fetchWithAuth(`/periop/api/messages/patient/${patientId}`);
    return response;
  } catch (error) {
    console.error('Error in getPatientMessages:', error);
    throw error;
  }
};

export const updateMessages = async (updates) => {
  try {
    return await fetchWithAuth('/periop/api/messages/batch-update', {
      method: 'PUT',
      body: JSON.stringify({ updates })
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Calendar-scoped dashboard metrics (Spanner). See server/app/services/analytics_operations.py.
 * @param {string} startIso - inclusive start (ISO 8601)
 * @param {string} endIso - exclusive end (ISO 8601), e.g. endOfRange + 1ms
 */
export async function fetchDashboardAnalytics(startIso, endIso) {
  const q = new URLSearchParams({ start: startIso, end: endIso });
  try {
    return await fetchWithAuth(`/periop/api/analytics/dashboard?${q.toString()}`);
  } catch (error) {
    console.error('fetchDashboardAnalytics:', error);
    throw error;
  }
}

export const saveTestCases = async (testCaseData) => {
  try {
    return await fetchWithAuth(`/periop/api/test-case-save`, {
      method: 'POST',
      body: JSON.stringify({
        patient_id: testCaseData.patient_id,
        recommendation_input: testCaseData.recommendation_input,
        test_cases: testCaseData.test_cases,
        timestamp: new Date().toISOString()
      })
    });
  } catch (error) {
    throw error;
  }
};
